import { Helmet } from "react-helmet";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Tabs,
  Tab,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import React, { useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";

import CustomTooltip from "../../../../components/CustomTooltip";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import MessageDialog from "../../../../components/MessageDialog";
import CommentDialog from "../../../../components/360Evaluation/CommentDialog";

import { ReactComponent as CalendarIcon } from "../../../../assets/icons/calendar-month.svg";
import { ReactComponent as TeamIcon } from "../../../../assets/images/people.svg";
import { ReactComponent as InnovationIcon } from "../../../../assets/images/position-icon.svg";
import { ReactComponent as SignIcon } from "../../../../assets/icons/pen.svg";
import { ReactComponent as InfoCircleIcon } from "../../../../assets/icons/info-circle.svg";
import { ReactComponent as SignaturesIcon } from "../../../../assets/images/home/signatures.svg";
import { ReactComponent as CommentIcon } from "../../../../assets/icons/chat.svg";
import { ReactComponent as DownIcon } from "../../../../assets/icons/down.svg";
import { ReactComponent as FileBrowserIcon } from "../../../../assets/icons/no-data.svg";
import { ReactComponent as FormIcon } from "../../../../assets/icons/form.svg";

import {
  getResultData,
  submitAndSignOff,
  getResultDataAdmin,
} from "../../../../actions/evaluation360";
import ReactECharts from "echarts-for-react";
import * as Util from "../../../../services/global.service";

const colorsPallet = ["#5DA196", "#77C47C", "#FF9921", "#8DCCF0", "#FFC24D"];

const styles = {
  link: {
    color: "#5DA196",
    cursor: "pointer",
  },
  end: {
    color: "#A3A3A3",
  },
  leftRotateLabel: {
    width: "40px",
    height: "100%",
    display: "flex",
    justifyContent: "center",
  },
  leftRotateLabelLineHeight: 8,
};

const useStyles = makeStyles({
  fillBlack: {
    "& > path": {
      fill: "#404349",
    },
    "& g > path": {
      fill: "#404349",
    },
  },
  strokeDisabled: {
    "& > path": {
      stroke: "rgba(0, 0, 0, 0.26)",
    },
    "& g > path": {
      stroke: "rgba(0, 0, 0, 0.26)",
    },
  },
  cardStyle: {
    borderRadius: "10px!important",
    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)!important",
  },
  barLegend: {
    fontSize: "14px",
    lineHeight: "19px",
  },
});

// bar total rating options
const totalRatingChartOptions = {
  grid: {
    width: "auto",
    height: "80%",
    left: 130,
    top: 10,
  },
  color: colorsPallet,
  tooltip: {
    show: true,
  },
  legend: {
    show: false,
  },
  xAxis: [
    {
      type: "value",
      boundaryGap: [0, 0.01],
      axisTick: {
        show: false,
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: "#404349",
        },
      },
      axisLabel: {
        show: false,
      },
    },
  ],
  yAxis: [
    {
      type: "category",
      data: [],
      axisTick: {
        show: false,
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: "#404349",
        },
      },
      axisLabel: {
        formatter: (value, index) => {
          let modValue = value.replace(/ *\([^)]*\) */g, "");
          if (
            modValue === "self" ||
            modValue === "ตนเอง" ||
            modValue.includes("others") ||
            modValue.includes("อื่นๆ")
          ) {
            return `${Util.capitalizedString(value)}`;
          } else {
            return `• ${Util.capitalizedString(value)}`;
          }
        },
        align: "left",
        margin: 130,
        fontFamily: "Kanit",
      },
      z: 100,
    },
  ],
  series: [
    {
      colorBy: "data",
      type: "bar",
      data: [],
      silent: true,
      itemStyle: {
        borderRadius: [0, 3, 3, 0],
      },
      barWidth: 25,
      label: {
        show: true,
        position: "insideRight",
        color: "#FFF",
        fontSize: 14,
        fontFamily: "Kanit",
      },
    },
  ],
};

// bar options
const barChartForEachItemOptions = {
  grid: {
    // width: "100%",
    height: "80%",
    left: 100,
    top: 10,
  },
  color: colorsPallet,
  tooltip: {
    show: false,
  },
  legend: {
    show: false,
  },
  xAxis: [
    {
      type: "value",
      boundaryGap: [0, 0.01],
      axisTick: {
        show: false,
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: "#404349",
        },
      },
      axisLabel: {
        show: false,
      },
      max: 5,
    },
  ],
  yAxis: [
    {
      type: "category",
      data: [],
      axisTick: {
        show: false,
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: "#404349",
        },
      },
      axisLabel: {
        formatter: (value, index) => {
          return `${Util.capitalizedString(value)}`;
        },
        align: "left",
        margin: 100,
        fontFamily: "Kanit",
      },
      z: 100,
    },
  ],
  series: [
    {
      colorBy: "data",
      type: "bar",
      data: [],
      silent: true,
      itemStyle: {
        borderRadius: [0, 3, 3, 0],
      },
      barWidth: 25,
      label: {
        show: true,
        position: "insideRight",
        color: "#FFF",
        fontSize: 14,
        fontFamily: "Kanit",
      },
    },
  ],
};

// radar options
const radarOptions = {
  tooltip: {
    show: true,
  },
  color: colorsPallet,
  legend: {
    show: false,
  },
  radar: {
    indicator: [],
    center: ["50%", "50%"],
    radius: 300,
    startAngle: 90,
    splitNumber: 5,
    splitArea: {
      show: false,
    },
    shape: "circle",
    axisName: {
      formatter: "{value}",
      color: "#404349",
      fontFamily: "Kanit",
      fontSize: 14,
    },
    axisLine: {
      lineStyle: {
        color: "#EEEEEE",
      },
    },
    splitLine: {
      lineStyle: {
        color: "#EEEEEE",
      },
    },
  },
  series: {
    type: "radar",
    symbolSize: 16,
    areaStyle: {
      shadowBlur: 0,
      opacity: 0.3,
    },
    emphasis: {
      lineStyle: {
        width: 4,
      },
    },
    data: [],
  },
};

// scatter options
const scatterOptions = {
  grid: {
    // width: "auto",
    left: 60,
  },
  color: colorsPallet,
  tooltip: {
    show: true,
  },
  legend: {
    show: false,
  },
  xAxis: [
    {
      position: "bottom",
      min: 0,
      max: 5,
      axisTick: {
        show: false,
      },
    },
  ],
  yAxis: [
    {
      position: "left",
      min: 0,
      max: 5,
      axisTick: {
        show: false,
      },
    },
  ],
  series: [],
};

const barWithMarkerChart = {
  grid: {
    // width: "80%",
    height: 380,
    left: 130,
    top: 0,
    containLabel: false,
  },
  color: colorsPallet,
  tooltip: {
    show: true,
    trigger: "axis",
    padding: 0,
    borderWidth: 0,
    extraCssText: "box-shadow: none;",
  },
  legend: {
    show: false,
  },
  xAxis: [
    {
      type: "value",
      boundaryGap: [0, 0.01],
      min: 0,
      max: 5,
      axisTick: {
        show: false,
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: "#404349",
        },
      },
      axisLabel: {
        show: false,
      },
    },
  ],
  yAxis: [
    {
      type: "category",
      data: ["-", "-", "-", "-", "-"],
      axisTick: {
        show: false,
      },
    },
  ],
  series: [
    {
      colorBy: "data",
      name: "Others",
      type: "bar",
      data: [0, 0, 0, 0, 0],
      itemStyle: {
        borderRadius: [0, 3, 3, 0],
      },
      barWidth: 25,
      label: {
        show: true,
        position: "insideRight",
        color: "#FFF",
        fontSize: 14,
        fontFamily: "Kanit",
      },
    },
    {
      name: "Self",
      type: "custom",
      data: [0, 0, 0, 0, 0],
      itemStyle: {
        borderWidth: 1.5,
      },
      renderItem: (params, api) => {
        let yValue = api.value(0);
        let valuesCoord = api.coord([yValue, api.value(1)]);
        let dottedLineStyle = api.style({
          stroke: "#404349",
          fill: undefined,
          lineDash: [2],
        });
        let circleStyle = api.style({
          fill: "#404349",
        });
        return {
          type: "group",
          children: [
            {
              type: "circle",
              transition: ["shape"],
              shape: {
                cx: valuesCoord[0],
                cy: valuesCoord[1] - 18,
                r: 3,
              },
              style: circleStyle,
            },
            {
              type: "line",
              transition: ["shape"],
              shape: {
                x1: valuesCoord[0],
                y1: valuesCoord[1] + 15,
                x2: valuesCoord[0],
                y2: valuesCoord[1] - 15,
              },
              style: dottedLineStyle,
            },
          ],
        };
      },
      encode: {
        x: 0,
        y: [1, 2],
      },
      z: 100,
    },
  ],
};

const BarLegend = (props) => {
  const { data, colors, type } = props;
  let row = [];
  const { t } = useTranslation();
  const filterData =
    type === "radar"
      ? data?.series?.data
      : data?.series?.filter((item) => !item?.ignore);

  const [legendActiveStatus, setLegendActiveStatus] = React.useState([]);

  const handleToggleLegend = (data, index) => {
    let tmpStatus = [...legendActiveStatus];
    tmpStatus[index] = !tmpStatus[index];
    setLegendActiveStatus(tmpStatus);
    props.toggleLegend(data);
  };

  const getRadarName = (index) => {
    if (
      filterData?.[index]?.name?.toLowerCase() === "self" ||
      filterData?.[index]?.name?.toLowerCase() === "ตนเอง"
    ) {
      return `${filterData?.[index]?.name}`;
    } else {
      return `${filterData?.[index]?.name} (${filterData?.[index]?.count ?? "-"
        })`;
    }
  };

  filterData.forEach((item, i) => {
    row.push(
      <Box
        key={item.name}
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          cursor: type === "radar" ? "pointer" : "default",
        }}
        onClick={() => (type === "radar" ? handleToggleLegend(item, i) : "")}
      >
        <Box sx={{ width: "10px", mr: 1 }}>
          <div
            className="bar-legend-circle"
            style={{ backgroundColor: colors[i] }}
          ></div>
        </Box>
        <Box
          sx={{
            width: "auto",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
          px="2.5px"
        >
          <Typography
            style={styles.barLegend}
            color={
              type !== "radar"
                ? "#404349"
                : legendActiveStatus?.[i]
                  ? "#404349"
                  : "#A0A0A0"
            }
          >
            {" "}
            {type === "radar" ? getRadarName(i) : item.name}
          </Typography>
        </Box>
      </Box>
    );
  });

  useEffect(() => {
    let status = [];
    filterData.forEach((item, i) => {
      status.push(true);
    });
    setLegendActiveStatus(status);
  }, []);
  return row;
};

const NoData = (props) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  return (
    <Card
      sx={{ width: "100%", p: "40px" }}
      classes={{ root: classes.cardStyle }}
    >
      <CardContent sx={{ p: "0!important", overflowY: "auto" }}>
        <Grid container>
          <Grid
            item
            xs={12}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <FileBrowserIcon />
          </Grid>
        </Grid>
        <Grid container mt={2}>
          <Grid
            item
            xs={12}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography fontWeight={600} fontSize={20} color="#888F9D">
              {t("360Evaluation.label.noDataTitle")}
            </Typography>
          </Grid>
        </Grid>
        <Grid container mt={1}>
          <Grid
            item
            xs={12}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography fontWeight={600} fontSize={16} color="#888F9D">
              {t("360Evaluation.label.noDataDesc")}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const sortOrder = ["self", "others", "superior", "subordinate", "peer"];

const Evaluation360Result = (props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { serial, type } = useParams();
  const [scaleMinMax, setScaleMinMax] = React.useState({});
  const [totalChartType, setTotalChartType] = React.useState("rating");
  const [summaryChartType, setSummaryChartType] = React.useState("rating");
  const [lowestFiveChartType, setLowestFiveChartType] =
    React.useState("rating");
  const [highestFiveChartType, setHighestFiveChartType] =
    React.useState("rating");
  const [scatterChartType, setScatterChartType] = React.useState("rating");
  const [totalRatingChart, setTotalRatingChart] = React.useState(
    totalRatingChartOptions
  );
  const [totalRatingData, setTotalRatingData] = React.useState({});
  const [currentTab, setCurrentTab] = React.useState("");
  const [categoriesTabs, setCategoriesTabs] = React.useState([]);
  const [colorBar, setColor] = React.useState(colorsPallet);
  const totalRatingRef = useRef(null);
  const radarChartRef = useRef(null);
  const scatterRef = useRef(null);
  const otherBarChartRef = useRef(null);
  const categoryBarChartRef = useRef(null);
  const [categoryBarChartWidth, setCategoryBarChartWidth] = React.useState(0);
  const [signOffBtn, setSignOffBtn] = React.useState({
    show: false,
    disabled: true,
  });
  const [formData, setFormData] = React.useState({
    name: "",
    serial: "",
  });
  const [candidateData, setCandidateData] = React.useState({
    name: "",
  });
  const [progressData, setProgressData] = React.useState([]);
  const [showProgressSection, setShowProgressSection] = React.useState(false);
  const [overviewData, setOverviewData] = React.useState({});
  const [commentData, setCommentData] = React.useState({});
  const [categoriesData, setCategoriesData] = React.useState([]);
  const [radarChartOptions, setRadarChartOptions] =
    React.useState(radarOptions);
  const [scatterChartOptions, setScatterChartOptions] =
    React.useState(scatterOptions);
  const [highestFiveChartOptions, setHighestFiveChartOptions] =
    React.useState(barWithMarkerChart);
  const [lowestFiveChartOptions, setLowestFiveChartOptions] =
    React.useState(barWithMarkerChart);
  const [currentCategoryData, setCurrentCategoryData] = React.useState([]);
  const [signOffData, setSignOffData] = React.useState({});
  const [isSignOff, setIsSignOff] = React.useState({
    manager: false,
    self: false,
  });

  const [eachItemChartType, setEachItemChartType] = React.useState([]);
  const [barChartForEachItemsData, setBarChartForEachItemsData] =
    React.useState([]);
  const [isSignOffConfirmDialogOpen, setIsSignOffConfirmDialogOpen] =
    React.useState(false);
  const [isSignOffSuccessDialogOpen, setIsSignOffSuccessDialogOpen] =
    React.useState(false);
  const [isCommentDialogOpen, setIsCommentDialogOpen] = React.useState(false);
  const [currentCommentDialogData, setCurrentCommentDialogData] =
    React.useState([]);
  const [isDataEmpty, setIsDataEmpty] = React.useState(true);
  const [totalRatingElemWidth, setTotalRatingElemWidth] = React.useState(0);
  const [scatterChartWidth, setScatterChartWidth] = React.useState(0);
  const [otherBarChartWidth, setOtherBarChartWidth] = React.useState(0);

  const handleSuccessSignoff = () => {
    setIsSignOffSuccessDialogOpen(false);
    fetchResultData();
  };

  const handleConfirmSignOff = () => {
    setIsSignOffConfirmDialogOpen(false);
    dispatch(submitAndSignOff(serial)).then((resp) => {
      setIsSignOffSuccessDialogOpen(true);
    });
  };

  const goto = (route) => {
    navigate(route, { replace: false });
  };

  const handleChangeChartType = (val, type, itemIdx = null) => {
    val = val?.target?.value;
    switch (type) {
      case "total":
        setTotalChartType(val);
        break;
      case "summary":
        setSummaryChartType(val);
        break;
      case "scatter":
        setScatterChartType(val);
        break;
      case "highest":
        setHighestFiveChartType(val);
        break;
      case "lowest":
        setLowestFiveChartType(val);
        break;
      case "item":
        let tmpArray = [...eachItemChartType];
        tmpArray = tmpArray.map((item, index) => {
          if (index === itemIdx) {
            return val;
          } else {
            return item;
          }
        });
        setEachItemChartType(tmpArray);
        break;
      default:
        break;
    }
  };

  const handleChangeCurrentTab = (_, val) => {
    setCurrentTab(val);
  };

  const mappingData = (data) => {
    // form data
    if (data?.form) {
      setFormData({
        name: data.form?.name ?? "Form Name",
        serial: data.form?.serial ?? "",
        dueDate: data?.due_date,
        editor: data?.editor,
      });
    }
    // progress data
    if (data?.progress) {
      let tmpProgressArray = [];
      data.progress.forEach((item, index) => {
        tmpProgressArray.push({
          evaluator: item?.evaluator ?? "",
          min: item?.min ?? 0,
          completed: item?.completed ?? 0,
        });
      });

      tmpProgressArray.sort((a, b) => {
        return sortOrder.indexOf(a.evaluator) - sortOrder.indexOf(b.evaluator);
      });
      setProgressData(tmpProgressArray);
      setShowProgressSection(true);
    }
    // scale min, max
    if (data?.scale) {
      setScaleMinMax(data.scale);
    }
    // candidate data
    if (data?.candidate) {
      setCandidateData(data.candidate);
    }
    // sign-off button
    if (data?.button) {
      let editor = data?.editor;
      let showSignoff = false;
      if (data?.button?.have_signoff) {
        if (editor === "self") {
          if (!data?.signoff?.self?.sign) {
            showSignoff = true;
          }
        } else if (editor === "manager") {
          if (!data?.signoff?.manager?.sign) {
            showSignoff = true;
          }
        }
      }
      setSignOffBtn({
        show: showSignoff,
        disabled: !data.button?.can_signoff ?? true,
      });
    }
    // total rating data
    if (data?.total_rating) {
      setTotalRatingData(data.total_rating);
    }
    // categories tab
    if (data?.components) {
      let tmpCatArray = [];
      let tmpCatData = [];
      data.components.forEach((item, index) => {
        if (index === 0) {
          setCurrentTab(item?.name);
          // overview data
          setOverviewData(item);
        } else if (item?.code === "comment") {
          setCommentData(item);
        } else {
          tmpCatData.push(item);
        }
        tmpCatArray.push({
          text: item?.name ?? "Category Name",
          value: item?.name,
          code: item?.code ?? "",
        });
      });
      setCategoriesData(tmpCatData);
      setCategoriesTabs(tmpCatArray);
    }
    // sign off
    if (data?.signoff) {
      setSignOffData(data.signoff);
    }
    setIsSignOff({
      manager: data?.signoff?.manager?.sign ?? false,
      self: data?.signoff?.self?.sign ?? false,
    });
  };

  const handleRadarChart = (data = null) => {
    let tmpRadarChartOptions = Object.assign({}, radarChartOptions);
    let radarDataSets = [];
    let radarLabels = [];
    let tmpRadarDataSets = JSON.parse(JSON.stringify(data?.datasets));
    tmpRadarDataSets.sort((a, b) => {
      return sortOrder.indexOf(a.label) - sortOrder.indexOf(b.label);
    });
    tmpRadarDataSets.forEach((item) => {
      radarDataSets.push({
        value:
          summaryChartType === "rating"
            ? item?.value ?? []
            : item?.percent ?? [],
        name: Util.capitalizedString(item?.label),
        count: item?.count ?? 0,
      });
    });
    radarLabels = data?.labels?.map((item, index) => {
      return { name: `${Util.capitalizedString(item)}` };
    });
    tmpRadarChartOptions = {
      ...tmpRadarChartOptions,
      radar: {
        ...tmpRadarChartOptions.radar,
        indicator: radarLabels,
      },
      series: {
        ...tmpRadarChartOptions.series,
        data: radarDataSets,
        tooltip: {
          padding: 0,
          borderWidth: 0,
          extraCssText: "box-shadow: none;",
          formatter: (params) => {
            let result = `<div style="display: inline-block; width: 220px; border-radius: 3px; overflow: hidden; box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15); font-family: Kanit;">
                        <div style="width: 100%; background-color: #67A79D; height: 35px; color: #FFF; padding: 8px; overflow: hidden; text-overflow: ellipsis; ">
                            <span style="width: 70%; color: #FFF; font-size: 16px; font-weight: 400; ">${params?.name}</span>
                        </div>
                        <div style="width: 100%; background-color: #F7F7F7; padding: 8px; display: flex; flex-direction: column;">`;
            radarLabels.forEach((label, index) => {
              result += `<div style="width: 100%; display: flex; ">
                                    <span style="width: 70%; color: #404349; font-size: 16px; font-weight: 400; overflow: hidden; text-overflow: ellipsis; ">${label.name
                }:</span>
                                    <span style="width: 30%; color: #404349; font-size: 16px; font-weight: 400; text-align: right">${params.value?.[index] ?? 0
                }${summaryChartType === "percentage" ? "%" : ""
                }</span>
                                </div> `;
            });
            result += `</div></div>`;
            return result;
          },
        },
      },
    };
    setRadarChartOptions(tmpRadarChartOptions);
  };

  const handleScatterChart = (data = null) => {
    let tmpScatterChartOptions = Object.assign({}, data ? data : scatterChartOptions);
    let scatterDataSets = [];
    let scatterLabels = JSON.parse(
      JSON.stringify(overviewData.scatter_chart?.labels)
    );
    let scatterResult = JSON.parse(
      JSON.stringify(overviewData.scatter_chart?.result)
    );
    let tmpScatterDataSets = JSON.parse(
      JSON.stringify(overviewData.scatter_chart?.datasets)
    );
    tmpScatterDataSets.sort((a, b) => {
      return sortOrder.indexOf(a.label) - sortOrder.indexOf(b.label);
    });
    let tmpValues = {};
    let optionsValues = {};
    tmpScatterDataSets.forEach((item, index) => {
      tmpValues = {
        value:
          scatterChartType === "rating"
            ? [item?.value?.others ?? 0, item?.value?.self ?? 0]
            : [item?.percent?.others ?? 0, item?.percent?.self ?? 0],
        options:
          scatterChartType === "rating"
            ? item?.value?.options
            : item?.percent?.options,
      };
      if (tmpValues?.options) {
        optionsValues = tmpValues.options;
      }
      const sortedOptionsValues = {};
      sortOrder.forEach((key) => {
        if (optionsValues.hasOwnProperty(key)) {
          sortedOptionsValues[key] = optionsValues[key];
        }
      });
      scatterDataSets.push({
        name: scatterLabels[index],
        symbolSize: 12,
        data: [
          {
            value: tmpValues.value,
          },
        ],
        type: "scatter",
        tooltip: {
          padding: 0,
          borderWidth: 0,
          extraCssText: "box-shadow: none;",
          show: true,
          formatter: (params) => {
            let result = `<div style="display: inline-block; width: 220px; border-radius: 3px; overflow: hidden; box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15); font-family: Kanit;">
                        <div style="width: 100%; background-color: #67A79D; height: 35px; color: #FFF; padding: 8px;">
                            <span style="width: 70%; color: #FFF; font-size: 16px; font-weight: 400;">${params?.seriesName}</span>
                        </div>
                        <div style="width: 100%; background-color: #F7F7F7; padding: 8px; display: flex; flex-direction: column;">`;
            for (const [key, value] of Object.entries(
              sortedOptionsValues ?? {}
            )) {
              result += `<div style="width: 100%; display: flex">`;
              if (key === "self") {
                result += `<span style="width: 70%; color: #404349; font-size: 16px; font-weight: 400;">${Util.capitalizedString(
                  key
                )}:</span>`;
              } else if (key === "others") {
                result += `<span style="width: 70%; color: #404349; font-size: 16px; font-weight: 400;">${Util.capitalizedString(
                  key
                )}:</span>`;
              } else {
                result += `<span style="width: 70%; color: #404349; font-size: 16px; font-weight: 400; padding-left: 15px;"> • ${Util.capitalizedString(
                  key
                )}:</span>`;
              }
              result += `<span style="width: 30%; color: #404349; font-size: 16px; font-weight: 400; text-align: right">${value}${scatterChartType === "percentage" ? "%" : ""
                }</span>
                                </div>`;
            }
            result += `</div></div>`;
            return result;
          },
        },
      });
    });

    let min =
      scatterChartType === "rating" ? overviewData.scatter_chart?.min ?? 0 : 0;
    let max =
      scatterChartType === "rating"
        ? overviewData.scatter_chart?.max ?? 5
        : 100;
    let scatterResultCrossLine = {
      x:
        scatterChartType === "rating"
          ? scatterResult?.x ?? 0
          : (parseFloat(scatterResult?.x ?? 0) /
            parseFloat(overviewData.scatter_chart?.max)) *
          100,
      y:
        scatterChartType === "rating"
          ? scatterResult?.y ?? 0
          : (parseFloat(scatterResult?.y ?? 0) /
            parseFloat(overviewData.scatter_chart?.max)) *
          100,
    };
    tmpScatterChartOptions = {
      ...tmpScatterChartOptions,
      xAxis: [
        {
          position: "bottom",
          min: min,
          max: max,
          axisTick: {
            show: false,
          },
          axisLabel: {
            fontFamily: "Kanit",
            formatter: (e) => {
              return `${e}${scatterChartType === "percentage" ? "%" : ""}`;
            },
          },
        },
      ],
      yAxis: [
        {
          position: "left",
          min: min,
          max: max,
          axisTick: {
            show: false,
          },
          axisLabel: {
            fontFamily: "Kanit",
            formatter: (e) => {
              return `${e}${scatterChartType === "percentage" ? "%" : ""}`;
            },
          },
        },
      ],
      series: [
        // real data
        ...scatterDataSets,
        // chart's border
        {
          ignore: true,
          type: "line",
          markLine: {
            silent: true,
            lineStyle: {
              type: "solid",
              color: "#404349",
            },
            data: [
              [
                {
                  coord: [0, scatterChartType === "rating" ? 5 : 100],
                  symbol: "none",
                },
                {
                  coord: scatterChartType === "rating" ? [5, 5] : [100, 100],
                  symbol: "none",
                },
              ],
              [
                {
                  coord: scatterChartType === "rating" ? [5, 5] : [100, 100],
                  symbol: "none",
                },
                {
                  coord: [scatterChartType === "rating" ? 5 : 100, 0],
                  symbol: "none",
                },
              ],
            ],
          },
        },
        // cross line value
        {
          ignore: true,
          type: "line",
          id: "line-scatter",
          markLine: {
            silent: true,
            lineStyle: {
              type: "solid",
              color: "#404349",
              width: 2,
            },
            data: [
              [
                {
                  coord: [0, scatterResultCrossLine.x],
                  symbol: "none",
                  label: {
                    formatter: `(${scatterResultCrossLine.x}${scatterChartType === "percentage" ? "%" : ""
                      })`,
                    position: "insideStartTop",
                    distance: [2, 5],
                    color: "#AAAAAA",
                    fontSize: 13,
                    fontFamily: "Kanit",
                  },
                },
                {
                  coord: [max, scatterResultCrossLine.x],
                  symbol: "none",
                },
              ],
              [
                {
                  coord: [scatterResultCrossLine.y, max],
                  symbol: "none",
                  label: {
                    formatter: `(${scatterResultCrossLine.y}${scatterChartType === "percentage" ? "%" : ""
                      })`,
                    position: "insideEndTop",
                    rotate: 360,
                    distance: [2, 2],
                    color: "#AAAAAA",
                    fontSize: 13,
                    fontFamily: "Kanit",
                  },
                },
                {
                  coord: [scatterResultCrossLine.y, 0],
                  symbol: "none",
                },
              ],
            ],
          },
        },
      ],
    };
    setScatterChartOptions(tmpScatterChartOptions);
  };

  const handleHighestFiveChart = () => {
    let tmpHighestChart = Object.assign({}, highestFiveChartOptions);
    let tmpHighestDatas = JSON.parse(JSON.stringify(overviewData.ranking?.top));
    let highestLabels = [];
    let highestDatas = [];
    let highestSelf = [];
    tmpHighestDatas =
      highestFiveChartType === "rating"
        ? tmpHighestDatas?.values ?? []
        : tmpHighestDatas?.percent ?? [];
    tmpHighestDatas.forEach((item) => {
      highestLabels.push(item?.name ?? "Item");
      highestDatas.push(
        item?.value?.others ? parseFloat(item.value.others) : 0
      );
      highestSelf.push(item?.value?.self ? parseFloat(item.value.self) : 0);
    });

    highestLabels = highestLabels.reverse();
    highestLabels = highestLabels.map((item) => {
      return {
        value: item,
        // textStyle: {
        //     width: 55,
        //     fontFamily: "Kanit",
        //     overflow: "truncate",
        //     ellipsis: "..."
        // }
      };
    });
    highestDatas = highestDatas.reverse();
    highestSelf = highestSelf.reverse();
    tmpHighestChart = {
      ...tmpHighestChart,
      tooltip: {
        ...tmpHighestChart.tooltip,
        formatter: (params) => {
          let result = `<div style="display: inline-block; min-width: 220px; border-radius: 3px; overflow: hidden; box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15); font-family: Kanit;">
                        <div style="width: 100%; background-color: #67A79D; height: 35px; color: #FFF; padding: 8px;">
                            <span style="width: 70%; color: #FFF; font-size: 16px; font-weight: 400;">${params?.[0]?.name}</span>
                        </div>
                        <div style="width: 100%; background-color: #F7F7F7; padding: 8px; display: flex; flex-direction: column;">`;
          result += `<div style="width: 100%; display: flex">
                                <span style="width: 70%; color: #404349; font-size: 16px; font-weight: 400;">${params?.[1]?.seriesName
            }:</span>
                                <span style="width: 30%; color: #404349; font-size: 16px; font-weight: 400; text-align: right">${params?.[1]?.data
            }${highestFiveChartType === "percentage" ? "%" : ""
            }</span>
                            </div>
                            <div style="width: 100%; display: flex">
                                <span style="width: 70%; color: #404349; font-size: 16px; font-weight: 400;">${params?.[0]?.seriesName
            }:</span>
                                <span style="width: 30%; color: #404349; font-size: 16px; font-weight: 400; text-align: right">${params?.[0]?.data
            }${highestFiveChartType === "percentage" ? "%" : ""
            }</span>
                            </div> `;
          result += `</div></div>`;
          return result;
        },
      },
      color: ["#FFE884", "#D7DF81", "#B7D67F", "#77C47C", "#57975C"],
      yAxis: [
        {
          ...tmpHighestChart.yAxis[0],
          type: "category",
          data: highestLabels,
          axisLabel: {
            width: 110,
            formatter: (e) => {
              return `${e}`;
            },
            fontFamily: "Kanit",
            overflow: "break",
          },
        },
      ],
      xAxis: [
        {
          ...tmpHighestChart.xAxis[0],
          max: highestFiveChartType === "rating" ? scaleMinMax?.max ?? 1 : 100,
        },
      ],
      series: [
        {
          ...tmpHighestChart.series[0],
          data: highestDatas,
          label: {
            ...tmpHighestChart.series[0]?.label,
            formatter: (e) => {
              if (highestFiveChartType === "percentage") {
                return `${e.value}%`;
              } else {
                return e.value;
              }
            },
          },
        },
        {
          ...tmpHighestChart.series[1],
          data: highestSelf,
        },
      ],
    };
    setHighestFiveChartOptions(tmpHighestChart);
  };

  const handleLowestFiveChart = () => {
    let tmpLowestChart = Object.assign({}, lowestFiveChartOptions);
    let tmpLowestDatas = JSON.parse(
      JSON.stringify(overviewData.ranking?.bottom)
    );
    let lowestLabels = [];
    let lowestDatas = [];
    let lowestSelf = [];
    tmpLowestDatas =
      lowestFiveChartType === "rating"
        ? tmpLowestDatas?.values ?? []
        : tmpLowestDatas?.percent ?? [];
    tmpLowestDatas.forEach((item) => {
      lowestLabels.push(item?.name ?? "Item");
      lowestDatas.push(item?.value?.others ? parseFloat(item.value.others) : 0);
      lowestSelf.push(item?.value?.self ? parseFloat(item.value.self) : 0);
    });

    lowestLabels = lowestLabels.reverse();
    lowestLabels = lowestLabels.map((item) => {
      return {
        value: item,
        // textStyle: {
        //     width: 55,
        //     fontFamily: "Kanit",
        //     overflow: "truncate",
        //     ellipsis: "..."
        // }
      };
    });
    lowestDatas = lowestDatas.reverse();
    lowestSelf = lowestSelf.reverse();

    tmpLowestChart = {
      ...tmpLowestChart,
      tooltip: {
        ...tmpLowestChart.tooltip,
        formatter: (params) => {
          let result = `<div style="display: inline-block; min-width: 220px; border-radius: 3px; overflow: hidden; box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15); font-family: Kanit;">
                        <div style="width: 100%; background-color: #67A79D; height: 35px; color: #FFF; padding: 8px;">
                            <span style="width: 70%; color: #FFF; font-size: 16px; font-weight: 400;">${params?.[0]?.name}</span>
                        </div>
                        <div style="width: 100%; background-color: #F7F7F7; padding: 8px; display: flex; flex-direction: column;">`;
          result += `<div style="width: 100%; display: flex">
                                <span style="width: 70%; color: #404349; font-size: 16px; font-weight: 400;">${params?.[1]?.seriesName
            }:</span>
                                <span style="width: 30%; color: #404349; font-size: 16px; font-weight: 400; text-align: right">${params?.[1]?.data
            }${lowestFiveChartType === "percentage" ? "%" : ""
            }</span>
                            </div>
                            <div style="width: 100%; display: flex">
                                <span style="width: 70%; color: #404349; font-size: 16px; font-weight: 400;">${params?.[0]?.seriesName
            }:</span>
                                <span style="width: 30%; color: #404349; font-size: 16px; font-weight: 400; text-align: right">${params?.[0]?.data
            }${lowestFiveChartType === "percentage" ? "%" : ""
            }</span>
                            </div> `;
          result += `</div></div>`;
          return result;
        },
      },
      color: ["#FDB379", "#FB7454", "#D34B4E", "#960B13", "#67000D"],
      yAxis: [
        {
          ...tmpLowestChart.yAxis[0],
          type: "category",
          data: lowestLabels,
          axisLabel: {
            width: 110,
            formatter: (e) => {
              return `${e}`;
            },
            fontFamily: "Kanit",
            overflow: "break",
          },
        },
      ],
      xAxis: [
        {
          ...tmpLowestChart.xAxis[0],
          max: lowestFiveChartType === "rating" ? scaleMinMax?.max ?? 1 : 100,
        },
      ],
      series: [
        {
          ...tmpLowestChart.series[0],
          data: lowestDatas,
          label: {
            ...tmpLowestChart.series[0]?.label,
            formatter: (e) => {
              if (lowestFiveChartType === "percentage") {
                return `${e.value}%`;
              } else {
                return e.value;
              }
            },
          },
        },
        {
          ...tmpLowestChart.series[1],
          data: lowestSelf,
        },
      ],
    };
    setLowestFiveChartOptions(tmpLowestChart);
  };

  const doToggleLegendRadarChart = (data) => {
    const chartAPI = radarChartRef?.current?.getEchartsInstance();
    if (chartAPI) {
      chartAPI.dispatchAction({
        type: "legendToggleSelect",
        name: data?.name,
      });
    }
  };

  const RadarChart = () => {
    return (
      <>
        <Grid container mb={2}>
          <Grid item xs={12}>
            <Typography fontSize={24} fontWeight={600} color="#404349">
              {currentTab}
            </Typography>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12}>
            <Card
              sx={{ width: "100%", p: "40px" }}
              classes={{ root: classes.cardStyle }}
            >
              <CardContent sx={{ p: "0!important", overflowY: "auto" }}>
                <Grid container>
                  <Grid item xs={6}>
                    <Grid container item display="flex" alignItems="center">
                      <Typography
                        fontWeight={600}
                        fontSize={24}
                        color="#404349"
                      >
                        {t("360Evaluation.label.summary")}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <Grid
                      container
                      item
                      display="flex"
                      alignItems="center"
                      sx={{ height: "36px" }}
                      justifyContent="end"
                    >
                      <ToggleButtonGroup
                        color="primary"
                        value={summaryChartType}
                        exclusive
                        onChange={(e) => handleChangeChartType(e, "summary")}
                        aria-label="Platform"
                        size="small"
                        sx={{
                          "& .MuiToggleButton-root": {
                            width: "85px",
                            height: "30px",
                            fontWeight: 400,
                            fontSize: 14,
                            textTransform: "none",
                          },
                          "& .Mui-selected": { borderColor: "#5DA196" },
                          "& .Mui-selected.MuiToggleButtonGroup-grouped:not(:first-of-type)":
                            { borderLeft: "1px solid #5DA196!important" },
                        }}
                      >
                        <ToggleButton value="rating">
                          {t("label.rating")}
                        </ToggleButton>
                        <ToggleButton value="percentage">
                          {t("label.percentage")}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container item>
                  <Grid container mt={1} mb={2}>
                    <Grid item xs={12} sx={{ height: "700px" }}>
                      <ReactECharts
                        option={radarChartOptions}
                        opts={{ renderer: "svg" }}
                        style={{ height: 680 }}
                        ref={radarChartRefCallback}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <BarLegend
                      data={radarChartOptions}
                      colors={colorBar}
                      type="radar"
                      toggleLegend={(data) => doToggleLegendRadarChart(data)}
                    ></BarLegend>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </>
    );
  };

  const handleShowComment = (comments) => {
    setCurrentCommentDialogData(comments);
    setIsCommentDialogOpen(true);
  };

  const fetchResultData = () => {
    if (type && type !== "") {
      dispatch(getResultDataAdmin(serial)).then((resp) => {
        mappingData(resp);
        setIsDataEmpty(resp?.is_empty);
      });
    } else {
      dispatch(getResultData(serial)).then((resp) => {
        mappingData(resp);
        setIsDataEmpty(resp?.is_empty);
      });
    }
  };


  const showWeight = () => {
    let weights = [];
    if (totalRatingData?.weight) {
      console.log(totalRatingData?.weight)
      for (const code in totalRatingData?.weight) {
        let value = totalRatingData.weight[code]
        if (value > 0) weights.push(Util.capitalizedString(code) + ' ' + value + '%')
      }
    }

    let label = weights.join(' / ')

    return (<span style={{ marginRight: 20 }}>{label}</span>)
  }

  // useEffect
  useEffect(() => {
    if (serial && serial !== "") fetchResultData();
  }, []);

  useEffect(() => {
    if (
      Object.keys(totalRatingData).length > 0 &&
      totalRatingData.constructor === Object &&
      totalChartType
    ) {
      // init total rating chart data
      let tmpRawData = JSON.parse(JSON.stringify(totalRatingData));
      tmpRawData =
        totalChartType === "rating"
          ? tmpRawData?.graph?.value
          : tmpRawData?.graph?.percent;
      let formatCategoriesAndSeriesData = [];
      let othersCountValues = 0;

      tmpRawData.forEach((item) => {
        formatCategoriesAndSeriesData.push({
          name: item?.name ?? "Item",
          value: item?.value ?? 0,
        });
        // if item is others get sub
        if (item?.options?.data) {
          item.options.data.forEach((optsItem) => {
            let tmpObj = {
              name: optsItem?.name ?? "Item",
              value: optsItem.value,
              count: 0,
            };
            // get count of each sub
            if (optsItem?.options?.count) {
              tmpObj.count = optsItem?.options?.count ?? 0;
              othersCountValues += tmpObj.count;
            }
            formatCategoriesAndSeriesData.push(tmpObj);
          });
        }
      });

      formatCategoriesAndSeriesData.sort((a, b) => {
        return sortOrder.indexOf(a.name) - sortOrder.indexOf(b.name);
      });
      formatCategoriesAndSeriesData = formatCategoriesAndSeriesData.reverse();
      let tmpTotalRatingChart = Object.assign({}, totalRatingChart);
      let datas = formatCategoriesAndSeriesData.map((item) =>
        parseFloat(item.value).toFixed(2)
      );
      let nullDatas = formatCategoriesAndSeriesData.map((item) => {
        if (item.value === null) {
          return 100;
        } else {
          return null;
        }
      });

      const setTextCoord = () => {
        let result = 230;
        if (i18n.language === "en-US") {
          if (showProgressSection) {
            result = 230;
          } else {
            result = 590;
          }
        } else {
          if (showProgressSection) {
            result = 230;
          } else {
            result = 660;
          }
        }
        return result;
      };
      let labelStyle =
        totalChartType === "percentage"
          ? {
            label: {
              efef: "dfdf",
            },
          }
          : null;
      let updatedTotalRatingChart = {
        ...tmpTotalRatingChart,
        grid: showProgressSection
          ? {
            // width: "80%",
            height: "80%",
            left: 130,
            top: 10,
          }
          : {
            // width: "89.6%", // still need to fix
            height: "80%",
            left: 130,
            top: 10,
          },
        yAxis: [
          {
            type: "category",
            data: formatCategoriesAndSeriesData.map((item) => {
              if (
                item.name.toLowerCase() === "others" ||
                item.name === "อื่นๆ"
              ) {
                return `${item.name} (${othersCountValues})`;
              } else if (
                item.name.toLowerCase() !== "self" &&
                item.name !== "ตนเอง"
              ) {
                return `${item.name} (${item.count})`;
              } else {
                return `${item.name}`;
              }
            }),
          },
        ],
        xAxis: [
          {
            ...tmpTotalRatingChart.xAxis[0],
            max: totalChartType === "percentage" ? 100 : scaleMinMax?.max ?? 1,
          },
          {
            ...tmpTotalRatingChart.xAxis[0],
            axisLine: {
              show: false,
            },
            max: 100,
          },
        ],
        series: [
          {
            ...tmpTotalRatingChart?.series?.[0],
            colorBy: "data",
            type: "bar",
            data: datas,
            itemStyle: {
              borderRadius: [0, 3, 3, 0],
            },
            label: {
              ...tmpTotalRatingChart?.series?.[0]?.label,
              formatter: (e) => {
                if (totalChartType === "percentage") {
                  return `${e.value}%`;
                } else {
                  return e.value;
                }
              },
            },
          },
          {
            name: "Self",
            type: "custom",
            silent: true,
            data: nullDatas,
            renderItem: (params, api) => {
              let xValue = api.value(0);
              let valuesCoord = api.coord([xValue, api.value(1)]);
              let dottedLineStyle = api.style({
                stroke: "#D8D8D8",
                fill: undefined,
                lineDash: [5],
              });
              if (!isNaN(xValue)) {
                return {
                  type: "group",
                  children: [
                    {
                      type: "line",
                      transition: ["shape"],
                      shape: {
                        x1: 135,
                        y1: valuesCoord[1] - 15,
                        x2: 2000,
                        y2: valuesCoord[1] - 15,
                      },
                      style: dottedLineStyle,
                    },
                    {
                      type: "text",
                      style: {
                        text:
                          i18n.language === "en-US"
                            ? "No. of evaluators did not reach the minimum requirement."
                            : "จำนวนผู้ประเมินน้อยกว่าขั้นต่ำที่กำหนดไว้",
                        x: setTextCoord(),
                        y: valuesCoord[1] - 5,
                        fill: "#BABABA",
                        font: '14px "Kanit", sans-serif',
                      },
                    },
                    {
                      type: "line",
                      transition: ["shape"],
                      shape: {
                        x1: 135,
                        y1: valuesCoord[1] + 15,
                        x2: 2000,
                        y2: valuesCoord[1] + 15,
                      },
                      style: dottedLineStyle,
                    },
                  ],
                };
              } else {
                return;
              }
            },
            encode: {
              x: 0,
              y: [1, 2],
            },
            z: -1,
          },
        ],
      };
      setTotalRatingChart(updatedTotalRatingChart);
    }
  }, [totalRatingData, totalChartType, showProgressSection]);

  const radarChartRefCallback = useCallback((node) => {
    if (node) {
      radarChartRef.current = node;
    }
  }, []);

  // set scatter chart width
  const scatterChartRefCallback = useCallback((node) => {
    if (node) {
      setScatterChartWidth(node?.offsetWidth ?? 0);
    }
  }, []);

  // set other bar chart width
  const otherBarChartRefCallback = useCallback((node) => {
    if (node) {
      setOtherBarChartWidth(node?.offsetWidth ?? 0);
    }
  }, []);

  // set bar chart for each category
  const categoryBarChartRefCallback = useCallback((node) => {
    if (node) {
      setCategoryBarChartWidth(node?.offsetWidth ?? 0);
    }
  }, []);


  useEffect(() => {
    setTotalRatingElemWidth(totalRatingRef.current.offsetWidth);
  }, [showProgressSection]);

  // set total chart width
  useEffect(() => {
    let tmpTotalRatingChart = Object.assign({}, totalRatingChart);
    let updatedTotalRatingChart = {
      ...tmpTotalRatingChart,
      grid: {
        ...tmpTotalRatingChart.grid,
        width: totalRatingElemWidth - 132,
      },
    };
    setTotalRatingChart(updatedTotalRatingChart);
  }, [totalRatingElemWidth]);

  useEffect(() => {
    if (scatterChartWidth > 0) {
      let tmpScatterChartOptions = Object.assign({}, scatterChartOptions);
      let updatedScatterChart = {
        ...tmpScatterChartOptions,
        grid: {
          ...tmpScatterChartOptions.grid,
          width: scatterChartWidth - 70,
        },
      };
      handleScatterChart(updatedScatterChart);
    }
  }, [scatterChartWidth]);

  useEffect(() => {
    let width = otherBarChartWidth - 134;
    // highest 5
    let tmpHighestChart = Object.assign({}, highestFiveChartOptions);
    let updatedHighestChart = {
      ...tmpHighestChart,
      grid: {
        ...tmpHighestChart.grid,
        width: width,
      },
    };
    setHighestFiveChartOptions(updatedHighestChart);
    // lowest 5
    let tmpLowestChart = Object.assign({}, lowestFiveChartOptions);
    let updatedLowestChart = {
      ...tmpLowestChart,
      grid: {
        ...tmpLowestChart.grid,
        width: width,
      },
    };
    setLowestFiveChartOptions(updatedLowestChart);
  }, [otherBarChartWidth]);
  useEffect(() => {
    let tmpBarChartForEachItem = [...barChartForEachItemsData];
    let resultUpdatedBarChartForEachItem = [];
    tmpBarChartForEachItem.forEach((item) => {
      resultUpdatedBarChartForEachItem.push({
        ...item,
        grid: {
          ...item.grid,
          width: categoryBarChartWidth - 105,
        },
      });
    });
    setBarChartForEachItemsData(resultUpdatedBarChartForEachItem);
  }, [categoryBarChartWidth]);

  useEffect(() => {
    if (overviewData?.summary) {
      // HIGHEST & LOWEST CHART
      // highest 5
      handleHighestFiveChart();
      // - lowest 5
      handleLowestFiveChart();
    }
  }, [overviewData]);

  useEffect(() => {
    // SCATTER CHART
    if (overviewData?.summary) {
      handleScatterChart();
    }
  }, [scatterChartType]);

  useEffect(() => {
    // highest 5
    if (overviewData?.summary) {
      handleHighestFiveChart();
    }
  }, [highestFiveChartType]);
  useEffect(() => {
    // - lowest 5
    if (overviewData?.summary) {
      handleLowestFiveChart();
    }
  }, [lowestFiveChartType]);

  useEffect(() => {
    if (categoriesTabs?.length > 0 && categoriesData?.length > 0) {
      if (
        currentTab !== "" &&
        currentTab !== "comment" &&
        currentTab !== categoriesTabs?.[0]?.value
      ) {
        let getCategoryData = categoriesData.find(
          (item) => item.name === currentTab
        );
        if (getCategoryData) {
          setCurrentCategoryData(getCategoryData?.items ?? []);
          let chartTypeArray = [];
          getCategoryData?.items?.forEach((_) => {
            // set chart type for each item
            chartTypeArray.push("rating");
          });
          setEachItemChartType(chartTypeArray);
          // RADAR CHART
          handleRadarChart(getCategoryData);
        }
      } else if (
        overviewData?.summary &&
        currentTab === categoriesTabs?.[0]?.value
      ) {
        handleRadarChart(overviewData.summary);
      }
    }
  }, [currentTab, categoriesTabs, summaryChartType, categoriesData, overviewData]);

  useEffect(() => {
    if (
      currentCategoryData &&
      currentCategoryData?.length > 0 &&
      eachItemChartType &&
      eachItemChartType?.length > 0
    ) {
      let resultChartArray = [];
      let values = 0;
      let formatCategoriesAndSeriesData = [];
      let updatedChartOptions = {};
      let datas = [];
      let tmpChart = {};
      currentCategoryData?.forEach((item, index) => {
        formatCategoriesAndSeriesData = [];
        item?.datasets?.forEach((subItem) => {
          values =
            eachItemChartType?.[index] === "rating"
              ? subItem?.value
              : subItem?.percent;
          formatCategoriesAndSeriesData.push({
            name: subItem?.label ?? "Item",
            value: values ?? 0,
          });
        });
        formatCategoriesAndSeriesData.sort((a, b) => {
          return sortOrder.indexOf(a.name) - sortOrder.indexOf(b.name);
        });
        formatCategoriesAndSeriesData = formatCategoriesAndSeriesData.reverse();
        tmpChart = Object.assign({}, barChartForEachItemOptions);
        datas = formatCategoriesAndSeriesData.map((item) =>
          parseFloat(item.value).toFixed(2)
        );
        updatedChartOptions = {
          ...tmpChart,
          yAxis: [
            {
              ...tmpChart.yAxis[0],
              type: "category",
              data: formatCategoriesAndSeriesData.map((item) => {
                return `${Util.capitalizedString(item.name)}`;
              }),
            },
          ],
          xAxis: [
            {
              ...tmpChart.xAxis[0],
              max: eachItemChartType?.[index] === "percentage" ? 100 : scaleMinMax?.max ?? 1,
            },
          ],
          series: [
            {
              ...tmpChart.series[0],
              data: datas,
              label: {
                ...tmpChart.series[0]?.label,
                formatter: (e) => {
                  if (eachItemChartType?.[index] === "percentage") {
                    return `${e.value}%`;
                  } else {
                    return e.value;
                  }
                },
              },
            },
          ],
        };
        resultChartArray.push(updatedChartOptions);
      });
      setBarChartForEachItemsData(resultChartArray);
    }
  }, [currentCategoryData, eachItemChartType]);

  return (
    <>
      <Helmet>
        <title>360 Evaluation - Result | PMS </title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: "background.default",
          minHeight: "100%",
          py: 3,
        }}
      >
        <Container maxWidth="xl">
          <Grid container pt={2} pb={2}>
            <Typography fontSize={16} lineHeight={1} color="#68717A">
              <span
                style={styles.link}
                onClick={() => navigate("/kpi/home", { replace: false })}
              >
                {t("label.home")}
              </span>{" "}
              /
              {formData?.editor === "manager" ? (
                <span
                  style={styles.link}
                  onClick={() =>
                    navigate(`/kpi/360evaluation-manager`, { replace: false })
                  }
                >
                  {` ${t("360Evaluation.label.Manage360Evaluation")}`}
                </span>
              ) : (
                <span
                  style={styles.link}
                  onClick={() =>
                    navigate(`/kpi/my360evaluation`, { replace: false })
                  }
                >
                  {` ${t("sidebar.label.my360evaluation")}`}
                </span>
              )}{" "}
              /
              <span style={styles.end}>
                {` ${t("360Evaluation.label.result")}`}
              </span>
            </Typography>
          </Grid>
          <Grid container mt={1} mb={2}>
            <Grid item display="flex" alignItems="center">
              <Typography fontSize={36} fontWeight="500" color="#404349">
                {formData.name}
              </Typography>
            </Grid>
            {signOffBtn.show && (
              <Grid item ml="auto" display="flex" alignItems="center">
                <Button
                  onClick={() => setIsSignOffConfirmDialogOpen(true)}
                  startIcon={
                    <SignIcon
                      className={
                        signOffBtn?.disabled ? `${classes.strokeDisabled}` : ""
                      }
                    />
                  }
                  sx={{
                    height: "45px",
                    textTransform: "none",
                    fontSize: "16px",
                  }}
                  variant="contained"
                  disabled={signOffBtn?.disabled}
                >
                  {t("360Evaluation.label.signOff")}
                </Button>
              </Grid>
            )}
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <Card
                sx={{ width: "100%", p: "40px" }}
                classes={{ root: classes.cardStyle }}
              >
                <CardContent sx={{ p: "0!important" }}>
                  <Grid container>
                    <Grid item xs={4}>
                      <Grid container item display="flex" alignItems="center">
                        <Typography
                          fontWeight={600}
                          fontSize={24}
                          color="#404349"
                        >
                          {t("evaluation.evaluationInformation")}
                        </Typography>
                      </Grid>
                      <Grid container mt={2} display="flex" alignItems="center">
                        <Grid item display="flex" alignItems="center" mr="10px">
                          <FormIcon
                            className={`${classes.fillBlack}`}
                          ></FormIcon>
                        </Grid>
                        <Grid item xs="auto" sm="auto" md="auto" mr={11}>
                          <Box>
                            <Typography
                              fontSize={18}
                              lineHeight={1.1}
                              color="#404349"
                            >
                              {t("evaluation.formName")}: {formData.name}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                      <Grid container mt={2} display="flex" alignItems="center">
                        <Grid item display="flex" alignItems="center" mr="10px">
                          <CalendarIcon
                            className={`${classes.fillBlack}`}
                          ></CalendarIcon>
                        </Grid>
                        <Grid item xs="auto" sm="auto" md="auto" mr={11}>
                          <Box>
                            <Typography
                              fontSize={18}
                              lineHeight={1.1}
                              color="#404349"
                            >
                              {t("360Evaluation.label.dueDate")}:{" "}
                              {Util.dateDayFormatAdmin(formData.dueDate)}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={8}>
                      <Grid
                        container
                        item
                        display="flex"
                        alignItems="center"
                        sx={{ height: "36px" }}
                      >
                        <Typography
                          fontSize={14}
                          fontWeight={500}
                          color="#AAAAAA"
                        >
                          {t("evaluation.candidateProfile")}
                        </Typography>
                      </Grid>
                      <Grid
                        container
                        display="flex"
                        alignItems="flex-start"
                        flexDirection="row"
                        mt={1}
                      >
                        <Grid
                          item
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          mr={3}
                        >
                          <Avatar
                            src={candidateData?.photo ?? ""}
                            sx={{
                              cursor: "pointer",
                              width: 50,
                              height: 50,
                            }}
                          />
                        </Grid>
                        <Grid item xs="auto">
                          <Grid
                            container
                            mb="3px"
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Grid item>
                              <Typography
                                fontSize={20}
                                fontWeight={600}
                                color="primary"
                              >
                                {candidateData?.fullname}
                              </Typography>
                            </Grid>
                          </Grid>
                          <Grid container>
                            <Grid
                              item
                              xs={12}
                              display="flex"
                              alignItems="center"
                            >
                              <Box
                                display="flex"
                                flexDirection="column"
                                justifyContent="center"
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  pt="5px"
                                >
                                  <CustomTooltip
                                    title={t(`label.position`)}
                                    placement="top"
                                  >
                                    <InnovationIcon
                                      style={{
                                        cursor: "pointer",
                                        width: "20px",
                                        height: "20px",
                                      }}
                                    ></InnovationIcon>
                                  </CustomTooltip>
                                  <Typography
                                    pl="10px"
                                    fontSize={18}
                                    fontWeight={400}
                                    color="#404349"
                                  >
                                    {candidateData?.position} (
                                    {Util.formatExpDate(
                                      candidateData?.pos_exp ?? "0y0m0d"
                                    )}
                                    )
                                  </Typography>
                                </Box>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  pt="5px"
                                >
                                  <CustomTooltip
                                    title={t(`label.team`)}
                                    placement="top"
                                  >
                                    <TeamIcon
                                      style={{
                                        cursor: "pointer",
                                        width: "20px",
                                        height: "20px",
                                      }}
                                    ></TeamIcon>
                                  </CustomTooltip>
                                  <Typography
                                    pl="10px"
                                    fontSize={18}
                                    fontWeight={400}
                                    color="#404349"
                                  >
                                    {candidateData?.organization}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Grid container mt={4} columnSpacing="30px">
            <Grid item xs={12} md={showProgressSection ? 6 : 12}>
              <Card
                sx={{ width: "100%", p: "40px", height: "350px" }}
                classes={{ root: classes.cardStyle }}
              >
                <CardContent sx={{ p: "0!important" }}>
                  <Grid container>
                    <Grid item xs={6}>
                      <Grid container item display="flex" alignItems="center">
                        <Typography
                          fontWeight={600}
                          fontSize={24}
                          color="#404349"
                        >
                          {t("360Evaluation.label.totalRating")}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid item xs={6}>
                      <Grid
                        container
                        item
                        display="flex"
                        alignItems="center"
                        sx={{ height: "36px" }}
                        justifyContent="end"
                      >
                        <ToggleButtonGroup
                          color="primary"
                          value={totalChartType}
                          exclusive
                          onChange={(e) => handleChangeChartType(e, "total")}
                          aria-label="Platform"
                          size="small"
                          sx={{
                            "& .MuiToggleButton-root": {
                              width: "85px",
                              height: "30px",
                              fontWeight: 400,
                              fontSize: 14,
                              textTransform: "none",
                            },
                            "& .Mui-selected": { borderColor: "#5DA196" },
                            "& .Mui-selected.MuiToggleButtonGroup-grouped:not(:first-of-type)":
                              { borderLeft: "1px solid #5DA196!important" },
                          }}
                        >
                          <ToggleButton value="rating">
                            {t("label.rating")}
                          </ToggleButton>
                          <ToggleButton value="percentage">
                            {t("label.percentage")}
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container mt={3} display="flex" alignItems="center">
                    <Grid
                      item
                      display="flex"
                      alignItems="center"
                      xs={12}
                      sx={{ height: "230px", position: "relative" }}
                      ref={totalRatingRef}
                    >
                      {/* bar chart with custom background */}
                      <ReactECharts
                        option={totalRatingChart}
                        opts={{ renderer: "svg" }}
                        style={{ height: "100%", width: "100%" }}
                      />
                      <Typography
                        fontSize={14}
                        fontWeight={600}
                        sx={{ position: "absolute", right: 0, bottom: "5px" }}
                      >

                        {showWeight()}

                        {t("label.total")}:{" "}
                        {totalChartType === "rating"
                          ? totalRatingData?.total?.value === null
                            ? `  -`
                            : totalRatingData?.total?.value ?? ""
                          : totalRatingData?.total?.percent === null
                            ? `  -`
                            : `${totalRatingData?.total?.percent}%` ?? ""}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            {showProgressSection && (
              <Grid item xs={12} md={6}>
                <Card
                  sx={{ width: "100%", p: "40px", height: "350px" }}
                  classes={{ root: classes.cardStyle }}
                >
                  <CardContent sx={{ p: "0!important", overflowY: "auto" }}>
                    <Grid container item>
                      <Grid container display="flex" alignItems="center">
                        <Grid item xs={12}>
                          <Typography
                            fontWeight={600}
                            fontSize={24}
                            color="#404349"
                          >
                            {t(
                              "360Evaluation.label.evaluatorsCompletedProgress"
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        mt={1}
                        mb={2}
                        sx={{ borderBottom: "1px solid #E1E1E1", pb: "2px" }}
                      >
                        <Grid item xs={4}>
                          <Typography
                            fontSize={12}
                            fontWeight={400}
                            color="#AAAAAA"
                          >
                            {t("360Evaluation.label.evaluator")}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography
                            fontSize={12}
                            fontWeight={400}
                            color="#AAAAAA"
                          >
                            {t("360Evaluation.label.displayMinEval")}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography
                            fontSize={12}
                            fontWeight={400}
                            color="#AAAAAA"
                          >
                            {t(
                              "360Evaluation.label.evaluatorsCompletedProgress"
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                      {progressData.map((progress, index) => {
                        return (
                          <Grid container mb={3} key={`progress-${index}`}>
                            <Grid item xs={4}>
                              <Typography
                                fontSize={16}
                                fontWeight={400}
                                color="#404349"
                              >
                                {progress?.evaluator
                                  ? Util.capitalizedString(progress.evaluator)
                                  : "Evaluator"}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography
                                fontSize={16}
                                fontWeight={400}
                                color="#404349"
                              >
                                {progress?.min}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography
                                fontSize={16}
                                fontWeight={400}
                                color={
                                  progress?.min > progress?.completed
                                    ? "#CC6058"
                                    : "#5DA196"
                                }
                              >
                                {progress?.completed}
                              </Typography>
                            </Grid>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
          <Grid container my={4}>
            <Grid item xs={12}>
              <Tabs
                value={currentTab}
                indicatorColor="primary"
                textColor="primary"
                onChange={handleChangeCurrentTab}
                aria-label="Tabs"
                variant="scrollable"
                allowScrollButtonsMobile
                TabIndicatorProps={{
                  style: {
                    height: "8px",
                    fontSize: "16px",
                  },
                }}
                style={{ borderBottom: "1px solid #DADADA" }}
              >
                {categoriesTabs?.map((item, index) => {
                  return (
                    <Tab
                      key={`cattab-${index}`}
                      sx={{ fontWeight: "400", fontSize: "16px" }}
                      label={
                        <span
                          className={classes.root}
                          style={{
                            textTransform: "none",
                            color:
                              currentTab !==
                                (item.code && item.code !== ""
                                  ? item.code
                                  : item.value)
                                ? "#404349"
                                : "#5DA196",
                          }}
                        >
                          {item.text}
                        </span>
                      }
                      value={
                        item.code && item.code !== "" ? item.code : item.value
                      }
                    />
                  );
                })}
              </Tabs>
            </Grid>
          </Grid>
          {/* each tab data */}
          {!isDataEmpty ? (
            currentTab === categoriesTabs?.[0]?.value ? (
              <>
                <RadarChart />
                <Grid container mt={4}>
                  <Grid item xs={12}>
                    <Card
                      sx={{ width: "100%", p: "40px" }}
                      classes={{ root: classes.cardStyle }}
                    >
                      <CardContent sx={{ p: "0!important", overflowY: "auto" }}>
                        <Grid container>
                          <Grid item xs={6}>
                            <Grid
                              container
                              item
                              display="flex"
                              alignItems="center"
                            >
                              <Typography
                                fontWeight={600}
                                fontSize={24}
                                color="#404349"
                              >
                                {t("360Evaluation.label.chart")}
                              </Typography>
                            </Grid>
                          </Grid>
                          <Grid item xs={6}>
                            <Grid
                              container
                              item
                              display="flex"
                              alignItems="center"
                              sx={{ height: "36px" }}
                              justifyContent="end"
                            >
                              <ToggleButtonGroup
                                color="primary"
                                value={scatterChartType}
                                exclusive
                                onChange={(e) =>
                                  handleChangeChartType(e, "scatter")
                                }
                                aria-label="Platform"
                                size="small"
                                sx={{
                                  "& .MuiToggleButton-root": {
                                    width: "85px",
                                    height: "30px",
                                    fontWeight: 400,
                                    fontSize: 14,
                                    textTransform: "none",
                                  },
                                  "& .Mui-selected": { borderColor: "#5DA196" },
                                  "& .Mui-selected.MuiToggleButtonGroup-grouped:not(:first-of-type)":
                                  {
                                    borderLeft: "1px solid #5DA196!important",
                                  },
                                }}
                              >
                                <ToggleButton value="rating">
                                  {t("label.rating")}
                                </ToggleButton>
                                <ToggleButton value="percentage">
                                  {t("label.percentage")}
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid container item>
                          <Grid container mt={1} mb={2}>
                            <Grid
                              item
                              xs={12}
                              sx={{ height: "400px", position: "relative" }}
                              p={0}
                              display="flex"
                              flexDirection="row"
                            >
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: "-10px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                }}
                              >
                                <Typography
                                  component="div"
                                  sx={{
                                    transform: "rotate(-90deg)",
                                    whiteSpace: "nowrap",
                                  }}
                                  fontSize="18px"
                                  fontWeight="500"
                                  color="#5DA196"
                                  lineHeight={styles.leftRotateLabelLineHeight}
                                >
                                  {t("360Evaluation.label.self")}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  position: "relative",
                                  height: "100%",
                                  width: "100%",
                                }}
                                ref={scatterChartRefCallback}
                              >
                                {/* scatter chart */}
                                <ReactECharts
                                  option={scatterChartOptions}
                                  opts={{ renderer: "svg" }}
                                  style={{ height: "100%", width: "100%" }}
                                />
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "absolute",
                                    left: 65,
                                    top: 61,
                                  }}
                                >
                                  <Typography
                                    fontSize={14}
                                    fontWeight={500}
                                    sx={{ mr: 1 }}
                                  >
                                    {t("360Evaluation.label.blindSpot")}
                                  </Typography>
                                  <CustomTooltip
                                    title={t(
                                      "360Evaluation.label.blindSpotDetail"
                                    )}
                                    placement="top"
                                  >
                                    <InfoCircleIcon
                                      style={{ width: "12px", height: "12px" }}
                                    />
                                  </CustomTooltip>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "absolute",
                                    right: 13,
                                    top: 61,
                                  }}
                                >
                                  <Typography
                                    fontSize={14}
                                    fontWeight={500}
                                    sx={{ mr: 1 }}
                                  >
                                    {t("360Evaluation.label.knownStrength")}
                                  </Typography>
                                  <CustomTooltip
                                    title={t(
                                      "360Evaluation.label.knownStrengthDetail"
                                    )}
                                    placement="top"
                                  >
                                    <InfoCircleIcon
                                      style={{ width: "12px", height: "12px" }}
                                    />
                                  </CustomTooltip>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "absolute",
                                    left: 65,
                                    top: 310,
                                  }}
                                >
                                  <Typography
                                    fontSize={14}
                                    fontWeight={500}
                                    sx={{ mr: 1 }}
                                  >
                                    {t(
                                      "360Evaluation.label.knownAreaForDevelopment"
                                    )}
                                  </Typography>
                                  <CustomTooltip
                                    title={t(
                                      "360Evaluation.label.knownAreaForDevelopmentDetail"
                                    )}
                                    placement="top"
                                  >
                                    <InfoCircleIcon
                                      style={{ width: "12px", height: "12px" }}
                                    />
                                  </CustomTooltip>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "absolute",
                                    right: 13,
                                    top: 310,
                                  }}
                                >
                                  <Typography
                                    fontSize={14}
                                    fontWeight={500}
                                    sx={{ mr: 1 }}
                                  >
                                    {t("360Evaluation.label.hiddenStrength")}
                                  </Typography>
                                  <CustomTooltip
                                    title={t(
                                      "360Evaluation.label.hiddenStrengthDetail"
                                    )}
                                    placement="top"
                                  >
                                    <InfoCircleIcon
                                      style={{ width: "12px", height: "12px" }}
                                    />
                                  </CustomTooltip>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  position: "absolute",
                                  bottom: 10,
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  pl: "40px",
                                }}
                              >
                                <Typography
                                  component="div"
                                  sx={{
                                    whiteSpace: "nowrap",
                                  }}
                                  fontSize="18px"
                                  fontWeight="500"
                                  color="#5DA196"
                                >
                                  {t("360Evaluation.label.other")}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid
                            item
                            xs={12}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <BarLegend
                              data={scatterChartOptions}
                              colors={colorBar}
                            ></BarLegend>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                <Grid container mt={4} columnSpacing="30px">
                  <Grid item xs={6}>
                    <Card
                      sx={{ width: "100%", p: "40px" }}
                      classes={{ root: classes.cardStyle }}
                    >
                      <CardContent sx={{ p: "0!important", overflowY: "auto" }}>
                        <Grid container>
                          <Grid item xs={6}>
                            <Grid
                              container
                              item
                              display="flex"
                              alignItems="center"
                            >
                              <Typography
                                fontWeight={600}
                                fontSize={24}
                                color="#404349"
                              >
                                {t("360Evaluation.label.highest")} 5
                              </Typography>
                            </Grid>
                          </Grid>
                          <Grid item xs={6}>
                            <Grid
                              container
                              item
                              display="flex"
                              alignItems="center"
                              sx={{ height: "36px" }}
                              justifyContent="end"
                            >
                              <ToggleButtonGroup
                                color="primary"
                                value={highestFiveChartType}
                                exclusive
                                onChange={(e) =>
                                  handleChangeChartType(e, "highest")
                                }
                                aria-label="Platform"
                                size="small"
                                sx={{
                                  "& .MuiToggleButton-root": {
                                    width: "85px",
                                    height: "30px",
                                    fontWeight: 400,
                                    fontSize: 14,
                                    textTransform: "none",
                                  },
                                  "& .Mui-selected": { borderColor: "#5DA196" },
                                  "& .Mui-selected.MuiToggleButtonGroup-grouped:not(:first-of-type)":
                                  {
                                    borderLeft: "1px solid #5DA196!important",
                                  },
                                }}
                              >
                                <ToggleButton value="rating">
                                  {t("label.rating")}
                                </ToggleButton>
                                <ToggleButton value="percentage">
                                  {t("label.percentage")}
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid container item>
                          <Grid container mt={2} mb={2}>
                            <Grid
                              item
                              xs={12}
                              sx={{ height: "400px", position: "relative" }}
                              p={0}
                              display="flex"
                              flexDirection="row"
                              ref={otherBarChartRefCallback}
                            >
                              {/* bar with marker chart */}
                              <ReactECharts
                                option={highestFiveChartOptions}
                                opts={{ renderer: "svg" }}
                                style={{ height: "100%", width: "100%" }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card
                      sx={{ width: "100%", p: "40px" }}
                      classes={{ root: classes.cardStyle }}
                    >
                      <CardContent sx={{ p: "0!important", overflowY: "auto" }}>
                        <Grid container>
                          <Grid item xs={6}>
                            <Grid
                              container
                              item
                              display="flex"
                              alignItems="center"
                            >
                              <Typography
                                fontWeight={600}
                                fontSize={24}
                                color="#404349"
                              >
                                {t("360Evaluation.label.lowest")} 5
                              </Typography>
                            </Grid>
                          </Grid>
                          <Grid item xs={6}>
                            <Grid
                              container
                              item
                              display="flex"
                              alignItems="center"
                              sx={{ height: "36px" }}
                              justifyContent="end"
                            >
                              <ToggleButtonGroup
                                color="primary"
                                value={lowestFiveChartType}
                                exclusive
                                onChange={(e) =>
                                  handleChangeChartType(e, "lowest")
                                }
                                aria-label="Platform"
                                size="small"
                                sx={{
                                  "& .MuiToggleButton-root": {
                                    width: "85px",
                                    height: "30px",
                                    fontWeight: 400,
                                    fontSize: 14,
                                    textTransform: "none",
                                  },
                                  "& .Mui-selected": { borderColor: "#5DA196" },
                                  "& .Mui-selected.MuiToggleButtonGroup-grouped:not(:first-of-type)":
                                  {
                                    borderLeft: "1px solid #5DA196!important",
                                  },
                                }}
                              >
                                <ToggleButton value="rating">
                                  {t("label.rating")}
                                </ToggleButton>
                                <ToggleButton value="percentage">
                                  {t("label.percentage")}
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid container item>
                          <Grid container mt={2} mb={2}>
                            <Grid
                              item
                              xs={12}
                              sx={{ height: "400px", position: "relative" }}
                              p={0}
                              display="flex"
                              flexDirection="row"
                            >
                              {/* bar with marker chart */}
                              <ReactECharts
                                option={lowestFiveChartOptions}
                                opts={{ renderer: "svg" }}
                                style={{ height: "100%", width: "100%" }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </>
            ) : currentTab === "comment" ? (
              <>
                <Grid container mt={4}>
                  <Grid item xs={12}>
                    <Typography fontWeight={600} fontSize={24} color="#404349">
                      {t("360Evaluation.label.comment")}
                    </Typography>
                  </Grid>
                </Grid>
                {commentData?.summary?.map((item, index) => {
                  return (
                    <Grid
                      container
                      mt={index === 0 ? 2 : 4}
                      key={`commentdata-${index}`}
                    >
                      <Grid item xs={12}>
                        <Card
                          sx={{ width: "100%", minHeight: "100px", p: "20px" }}
                          classes={{ root: classes.cardStyle }}
                        >
                          <CardContent
                            sx={{ p: "0!important", overflowY: "auto" }}
                          >
                            <Grid container>
                              <Grid item xs={12}>
                                <Grid
                                  container
                                  item
                                  display="flex"
                                  alignItems="center"
                                >
                                  <Typography
                                    fontWeight={500}
                                    fontSize={16}
                                    color="#404349"
                                  >
                                    {`Q${index + 1}: ${item?.topic ?? "-"}`}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                            {item?.comment?.length > 0 ? (
                              item?.comment?.map((comment, indexC) => {
                                return (
                                  <Grid
                                    container
                                    mt={2}
                                    key={`comment-${indexC}`}
                                  >
                                    <Grid item xs={12}>
                                      <Grid
                                        container
                                        item
                                        display="flex"
                                        alignItems="center"
                                      >
                                        <Box
                                          sx={{
                                            background: "#EFF6F4",
                                            borderRadius: "0px 20px 20px 20px",
                                            p: "10px",
                                          }}
                                        >
                                          <Typography
                                            fontWeight={400}
                                            fontSize={16}
                                            color="#404349"
                                          >
                                            {comment}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                );
                              })
                            ) : (
                              <Grid container mt={2}>
                                <Grid
                                  item
                                  xs={12}
                                  display="flex"
                                  alignItems="center"
                                  flexDirection="column"
                                >
                                  <Grid
                                    container
                                    item
                                    display="flex"
                                    alignItems="center"
                                    flexDirection="column"
                                  >
                                    <CommentIcon />
                                    <Typography
                                      fontWeight={600}
                                      fontSize={20}
                                      color="#888F9D"
                                      sx={{ mt: 1 }}
                                    >
                                      {t(
                                        "360Evaluation.label.noFeedbackFounded"
                                      )}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  );
                })}
              </>
            ) : (
              <>
                <RadarChart />
                {currentCategoryData.map((item, index) => {
                  return (
                    <>
                      <Grid
                        container
                        mt={4}
                        columnSpacing="30px"
                        key={`currentcat-${index}`}
                      >
                        <Grid item xs={12}>
                          <Typography
                            fontWeight={600}
                            fontSize={16}
                            color="#404349"
                          >
                            {item?.name ?? "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid container mt={2} columnSpacing="30px">
                        <Grid item xs={6}>
                          <Card
                            sx={{ width: "100%", p: "20px", height: "340px" }}
                            classes={{ root: classes.cardStyle }}
                          >
                            <CardContent
                              sx={{ p: "0!important", overflowY: "auto" }}
                            >
                              <Grid container>
                                <Grid item xs={6}>
                                  <Grid
                                    container
                                    item
                                    display="flex"
                                    alignItems="center"
                                  >
                                    <Typography
                                      fontWeight={600}
                                      fontSize={24}
                                      color="#404349"
                                    >
                                      {t("360Evaluation.label.chart")}
                                    </Typography>
                                  </Grid>
                                </Grid>
                                <Grid item xs={6}>
                                  <Grid
                                    container
                                    item
                                    display="flex"
                                    alignItems="center"
                                    sx={{ height: "36px" }}
                                    justifyContent="end"
                                  >
                                    <ToggleButtonGroup
                                      color="primary"
                                      value={eachItemChartType?.[index]}
                                      exclusive
                                      onChange={(e) =>
                                        handleChangeChartType(e, "item", index)
                                      }
                                      aria-label="Platform"
                                      size="small"
                                      sx={{
                                        "& .MuiToggleButton-root": {
                                          width: "85px",
                                          height: "30px",
                                          fontWeight: 400,
                                          fontSize: 14,
                                          textTransform: "none",
                                        },
                                        "& .Mui-selected": {
                                          borderColor: "#5DA196",
                                        },
                                        "& .Mui-selected.MuiToggleButtonGroup-grouped:not(:first-of-type)":
                                        {
                                          borderLeft:
                                            "1px solid #5DA196!important",
                                        },
                                      }}
                                    >
                                      <ToggleButton value="rating">
                                        {t("label.rating")}
                                      </ToggleButton>
                                      <ToggleButton value="percentage">
                                        {t("label.percentage")}
                                      </ToggleButton>
                                    </ToggleButtonGroup>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid container item>
                                <Grid container mt={2} mb={2}>
                                  <Grid
                                    item
                                    xs={12}
                                    sx={{
                                      height: "280px",
                                      position: "relative",
                                    }}
                                    p={0}
                                    display="flex"
                                    flexDirection="row"
                                    ref={categoryBarChartRefCallback}
                                  >
                                    {/* bar with marker chart */}
                                    {barChartForEachItemsData &&
                                      barChartForEachItemsData?.length > 0 && (
                                        <ReactECharts
                                          option={
                                            barChartForEachItemsData?.[index] ??
                                            barChartForEachItemOptions
                                          }
                                          opts={{ renderer: "svg" }}
                                          style={{
                                            height: "100%",
                                            width: "100%",
                                          }}
                                        />
                                      )}
                                  </Grid>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={6}>
                          <Card
                            sx={{
                              width: "100%",
                              p: "20px",
                              height: "340px",
                              overflowY: "hidden",
                            }}
                            classes={{ root: classes.cardStyle }}
                          >
                            <CardContent
                              sx={{
                                p: "0!important",
                                overflowY: "hidden",
                                height: "100%",
                                position: "relative",
                              }}
                            >
                              <Grid container>
                                <Grid item xs={12}>
                                  <Typography
                                    fontWeight={600}
                                    fontSize={24}
                                    color="#404349"
                                  >
                                    {t("360Evaluation.label.comment")}
                                  </Typography>
                                </Grid>
                              </Grid>
                              <Grid
                                container
                                item
                                xs={12}
                                sx={{
                                  height: "calc(100% - 60px)",
                                  overflow: "hidden",
                                }}
                                alignContent="flex-start"
                              >
                                {item?.comment?.length > 0 ? (
                                  item?.comment?.map((comment, indexC) => {
                                    return (
                                      <Grid
                                        container
                                        mt={2}
                                        key={`comment2-${indexC}`}
                                      >
                                        <Grid
                                          container
                                          item
                                          display="flex"
                                          alignItems="flex-start"
                                          flexDirection="column"
                                        >
                                          <Box
                                            sx={{
                                              background: "#EFF6F4",
                                              borderRadius:
                                                "0px 20px 20px 20px",
                                              p: "10px",
                                            }}
                                          >
                                            <Typography
                                              fontWeight={400}
                                              fontSize={16}
                                              color="#404349"
                                            >
                                              {comment}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      </Grid>
                                    );
                                  })
                                ) : (
                                  <Grid container mt={2}>
                                    <Grid item xs={12}>
                                      <Grid
                                        container
                                        item
                                        display="flex"
                                        alignItems="center"
                                        flexDirection="column"
                                      >
                                        <CommentIcon />
                                        <Typography
                                          fontWeight={600}
                                          fontSize={20}
                                          color="#888F9D"
                                          sx={{ mt: 1 }}
                                        >
                                          {t(
                                            "360Evaluation.label.noFeedbackFounded"
                                          )}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                )}
                              </Grid>
                              {item?.comment?.length > 0 && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    bottom: 5,
                                    width: "100%",
                                  }}
                                >
                                  <div
                                    style={{
                                      cursor: "pointer",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      display: "flex",
                                      flexDirection: "row",
                                      width: "fit-content",
                                      marginLeft: "auto",
                                      marginRight: "auto",
                                    }}
                                    onClick={() =>
                                      handleShowComment(item?.comment)
                                    }
                                  >
                                    <Typography
                                      fontWeight={400}
                                      fontSize={12}
                                      color="#5DA196"
                                      sx={{ textDecoration: "underline" }}
                                    >
                                      {t("360Evaluation.label.showComment")}
                                    </Typography>
                                    <DownIcon style={{ marginLeft: "5px" }} />
                                  </div>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </>
                  );
                })}
              </>
            )
          ) : (
            <NoData />
          )}
          {(isSignOff?.manager || isSignOff?.self) && (
            <>
              <Divider sx={{ borderColor: "#E4E4E4", my: 4 }} />
              <Grid container mb="30px">
                <Grid
                  item
                  md={6}
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: "5px",
                        background: "#2A8A71",
                        width: 48,
                        height: 48,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <SignaturesIcon />
                    </Box>
                    <Typography
                      variant="h2"
                      pl="20px"
                      fontSize={30}
                      lineHeight="33px"
                      color="#404349"
                    >
                      {t("evaluation.evaluationSignatures")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid container item sx={{ display: "block" }}>
                  <Box>
                    <Typography
                      pt="20px"
                      pb="32px"
                      fontSize="18px"
                      lineHeight={1.1}
                      fontWeight="400"
                      color="#404349"
                    >
                      {t("evaluation.subTilteEvaluationSignatures")}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      padding: "31px 0",
                      background: "#FFFFFF",
                      border: "1px solid #E4E4E4",
                      boxSizing: "border-box",
                      borderRadius: "10px",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: "calc(50% - 25px)",
                        maxWidth: "611px",
                        textAlign: "center",
                        margin: "0 25px",
                      }}
                      px="25px"
                    >
                      <Typography
                        fontSize="16px"
                        lineHeight="24px"
                        fontWeight="400"
                        color="#A0A0A0"
                        pb="5px"
                      >
                        {t("evaluation.evaluatorSignature")}
                      </Typography>
                      <Typography
                        fontSize="20px"
                        lineHeight="24px"
                        fontWeight="500"
                        color="#5DA196"
                        pb="15px"
                      >
                        {Util.formatData(
                          signOffData?.manager?.fullname ?? "name lastname",
                          "-"
                        )}
                      </Typography>
                      <Typography
                        fontSize="18px"
                        lineHeight="19.8px"
                        fontWeight="400"
                        color="#404349"
                        sx={{ textAlign: "center" }}
                      >
                        {t("evaluation.agreeToContentEvaluator")}
                      </Typography>
                      <Box
                        sx={{
                          pt: "37px",
                          pb: "26px",
                          "& div > input": {
                            textAlign: "center",
                            fontSize: "18px",
                            lineHeight: "26.91px",
                            p: "17px 14px",
                            color: "#404349",
                          },
                        }}
                      >
                        {isSignOff?.manager ? (
                          <Typography
                            fontSize="18px"
                            lineHeight="26.91px"
                            fontWeight="500"
                            color="#404349"
                            sx={{ textTransform: "uppercase" }}
                          >
                            {signOffData?.manager?.fullname}
                          </Typography>
                        ) : (
                          <Typography
                            fontSize="18px"
                            lineHeight="26.91px"
                            fontWeight="400"
                            color="#C4C4C4"
                          >
                            {t("evaluation.notSignedYet")}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        fontSize="18px"
                        lineHeight="19.8px"
                        fontWeight="400"
                        color="#A0A0A0"
                      >
                        ({t("evaluation.signOffDate")}:{" "}
                        {Util.formatData(
                          Util.dateDayFormat(signOffData?.manager?.updated_at),
                          "-"
                        )}
                        )
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: "calc(50% - 25px)",
                        maxWidth: "611px",
                        textAlign: "center",
                        margin: "0 25px",
                      }}
                      px="25px"
                    >
                      <Typography
                        fontSize="16px"
                        lineHeight="24px"
                        fontWeight="400"
                        color="#A0A0A0"
                        pb="5px"
                      >
                        {t("evaluation.candidateSignature")}
                      </Typography>
                      <Typography
                        fontSize="20px"
                        lineHeight="24px"
                        fontWeight="500"
                        color="#5DA196"
                        pb="15px"
                      >
                        {Util.formatData(
                          signOffData?.self?.fullname ?? "name lastname",
                          "-"
                        )}
                      </Typography>
                      <Typography
                        fontSize="18px"
                        lineHeight="19.8px"
                        fontWeight="400"
                        color="#404349"
                        sx={{ textAlign: "center" }}
                      >
                        {t("evaluation.agreeToContentEmployee")}
                      </Typography>
                      <Box
                        sx={{
                          pt: "37px",
                          pb: "26px",
                          "& div > input": {
                            textAlign: "center",
                            fontSize: "18px",
                            lineHeight: "26.91px",
                            p: "17px 14px",
                            color: "#404349",
                          },
                        }}
                      >
                        {isSignOff?.self ? (
                          <Typography
                            fontSize="18px"
                            lineHeight="26.91px"
                            fontWeight="500"
                            color="#404349"
                            sx={{ textTransform: "uppercase" }}
                          >
                            {signOffData?.self?.fullname}
                          </Typography>
                        ) : (
                          <Typography
                            fontSize="18px"
                            lineHeight="26.91px"
                            fontWeight="400"
                            color="#C4C4C4"
                          >
                            {t("evaluation.notSignedYet")}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        fontSize="18px"
                        lineHeight="19.8px"
                        fontWeight="400"
                        color="#A0A0A0"
                      >
                        ({t("evaluation.signOffDate")}:{" "}
                        {Util.formatData(
                          Util.dateDayFormat(signOffData?.self?.updated_at),
                          "-"
                        )}
                        )
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}

          <ConfirmDialog
            open={isSignOffConfirmDialogOpen}
            warning={true}
            submit={true}
            doConfirm={() => handleConfirmSignOff()}
            setValue={() => setIsSignOffConfirmDialogOpen(false)}
            title={t("360Evaluation.dialog.confirmTitle")}
            text={t("360Evaluation.dialog.confirmDesc")}
          />
          <MessageDialog
            open={isSignOffSuccessDialogOpen}
            setValue={() => handleSuccessSignoff()}
            title={t("360Evaluation.dialog.completeSignOffTitle")}
            text={t("360Evaluation.dialog.completeSignOffDesc")}
          />
          <CommentDialog
            open={isCommentDialogOpen}
            data={currentCommentDialogData}
            setValue={() => setIsCommentDialogOpen(false)}
          />
        </Container>
      </Box>
    </>
  );
};
export default Evaluation360Result;
